import { type KeyboardEvent, useCallback } from 'react'

import { Icon, Space, SubText, WithEllipsisTooltip } from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useFormatStaticAsset } from '@webapp/core/hooks/useFormatStaticAsset'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { CatalogItemDeliveryLabel } from '../CatalogItemDeliveryLabel'
import { CatalogItemRating } from '../CatalogItemRating'
import { CatalogItemTag, type ICatalogItemTagProps } from '../CatalogItemTag'
import { VendorLogoBadge } from '../VendorLogoBadge'
import { CatalogItemBodyStyled } from './styles/CatalogItemBodyStyled'
import { CatalogItemContentStyled } from './styles/CatalogItemContentStyled'
import { CatalogItemFulfillmentLogoStyled } from './styles/CatalogItemFulfillmentLogoStyled'
import { CatalogItemImageStyled } from './styles/CatalogItemImageStyled'
import { CatalogItemImageWrapperStyled } from './styles/CatalogItemImageWrapperStyled'
import { CatalogItemLayoutStyled } from './styles/CatalogItemLayoutStyled'
import { CatalogItemPriceStyled } from './styles/CatalogItemPriceStyled'
import { CatalogItemTitleStyled } from './styles/CatalogItemTitleStyled'
import { CatalogItemTagVariants } from '../../value-objects/CatalogItemTag'
import {
  type ConversationCatalogItemDelivery,
  type ConversationCatalogItemRating,
  type ConversationCatalogItemVendorBadge,
  type ConversationVendor,
} from '../../value-objects/ConversationTurnItem'
import { CatalogItemVendorBadgeSlots } from '../../value-objects/enums'

const BADGE_SLOT_VARIANTS = {
  [CatalogItemVendorBadgeSlots.PRIMARY]: CatalogItemTagVariants.STRONG,
  [CatalogItemVendorBadgeSlots.SUSTAINABILITY]: CatalogItemTagVariants.MUTED,
} as const

const INFO_BADGE_SLOTS = Object.keys(
  BADGE_SLOT_VARIANTS
) as (keyof typeof BADGE_SLOT_VARIANTS)[]

export interface ICatalogItemProps {
  img?: string
  title: string
  body: string
  tags: ICatalogItemTagProps[]
  price: number
  currency: CurrencyName
  isSelected?: boolean
  vendor?: ConversationVendor
  rating?: ConversationCatalogItemRating
  vendorBadges?: ConversationCatalogItemVendorBadge[]
  delivery?: ConversationCatalogItemDelivery
  listPrice?: number
  onClick: () => void
}

export const CatalogItem = ({
  img,
  title,
  body,
  tags,
  price,
  currency,
  isSelected = false,
  vendor,
  rating,
  vendorBadges = [],
  delivery,
  listPrice,
  onClick,
}: ICatalogItemProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const formatStaticAsset = useFormatStaticAsset()

  const infoBadges = INFO_BADGE_SLOTS.map((slot) => {
    const badge = vendorBadges.find((candidate) => candidate.slot === slot)
    return badge ? { ...badge, variant: BADGE_SLOT_VARIANTS[slot] } : null
  }).filter((badge): badge is NonNullable<typeof badge> => badge !== null)
  const fulfillmentBadge = vendorBadges.find(
    (badge) =>
      badge.slot === CatalogItemVendorBadgeSlots.FULFILLMENT && badge.logoUrl
  )
  const dealBadge = vendorBadges.find(
    (badge) => badge.slot === CatalogItemVendorBadgeSlots.DEAL
  )
  const hasSourceRow = Boolean(vendor?.logoUrl || delivery || fulfillmentBadge)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    },
    [onClick]
  )

  return (
    <CatalogItemLayoutStyled
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role='button'
      tabIndex={0}
      aria-pressed={isSelected}
      $isSelected={isSelected}
    >
      <CatalogItemImageWrapperStyled>
        {img ? (
          <CatalogItemImageStyled src={formatMedia(img)} alt='' />
        ) : (
          <Icon icon='gallery' size={24} color='darkGrey' aria-hidden />
        )}
      </CatalogItemImageWrapperStyled>
      <CatalogItemContentStyled>
        <Space direction='vertical' size='medium'>
          <Space direction='horizontal' size='medium' align='start'>
            <Space.Item grow={1} minWidth={0}>
              <Space direction='vertical' size='xsmall'>
                <WithEllipsisTooltip title={title}>
                  <CatalogItemTitleStyled
                    noWrap
                    textEllipsis
                    className='fs-mask'
                  >
                    {title}
                  </CatalogItemTitleStyled>
                </WithEllipsisTooltip>
                {(rating || infoBadges.length > 0) && (
                  <Space
                    direction='horizontal'
                    size='small'
                    align='center'
                    flexWrap
                  >
                    {rating && (
                      <CatalogItemRating
                        value={rating.value}
                        count={rating.count}
                        scaleMax={rating.scaleMax}
                      />
                    )}
                    {infoBadges.map((badge) => (
                      <CatalogItemTag
                        key={badge.slot}
                        icon={badge.icon}
                        text={badge.text}
                        logoUrl={badge.logoUrl}
                        variant={badge.variant}
                      />
                    ))}
                  </Space>
                )}
                <CatalogItemBodyStyled className='fs-mask'>
                  {body}
                </CatalogItemBodyStyled>
              </Space>
            </Space.Item>
            <Space.Item minWidth='fit-content'>
              <Space
                direction='vertical'
                size='xsmall'
                align='end'
                justify='center'
              >
                {dealBadge && (
                  <CatalogItemTag
                    text={dealBadge.text}
                    variant={CatalogItemTagVariants.DEAL}
                  />
                )}
                <CatalogItemPriceStyled className='fs-mask'>
                  <Price inline price={price} currency={currency} />
                </CatalogItemPriceStyled>
                {!!listPrice && (
                  <SubText
                    inline
                    noWrap
                    color='textSecondary'
                    className='fs-mask'
                  >
                    {intl.formatMessage(messages.listPriceLabel)}{' '}
                    <s>
                      <Price inline price={listPrice} currency={currency} />
                    </s>
                  </SubText>
                )}
              </Space>
            </Space.Item>
          </Space>
          {hasSourceRow && (
            <Space direction='horizontal' size='small' align='center'>
              {vendor?.logoUrl && <VendorLogoBadge vendor={vendor} />}
              {delivery && (
                <CatalogItemDeliveryLabel noWrap delivery={delivery} />
              )}
              {fulfillmentBadge?.logoUrl && (
                <CatalogItemFulfillmentLogoStyled
                  src={formatStaticAsset(fulfillmentBadge.logoUrl)}
                  alt={fulfillmentBadge.text}
                />
              )}
            </Space>
          )}
          {tags.length > 0 && (
            <Space direction='horizontal' size='small' align='center' flexWrap>
              {tags.map((tag, index) => (
                <CatalogItemTag
                  key={`${index}-${tag.text}`}
                  icon={tag.icon}
                  text={tag.text}
                  variant={tag.variant}
                />
              ))}
            </Space>
          )}
        </Space>
      </CatalogItemContentStyled>
    </CatalogItemLayoutStyled>
  )
}
